import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'
import { Tooltip } from '@pay-com/components'
import { Questionmark } from '@pay-com/icons'

type TooltipFieldContextValue = {
  activeId: string | null
  setActiveId: (id: string | null) => void
}

const TooltipFieldContext = createContext<TooltipFieldContextValue>({
  activeId: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setActiveId: () => {}
})

export const TooltipFieldProvider = ({ children }: { children: ReactNode }) => {
  const [activeId, setActiveId] = useState<string | null>(null)
  const value = useMemo(() => ({ activeId, setActiveId }), [activeId])
  return (
    <TooltipFieldContext.Provider value={value}>
      {children}
    </TooltipFieldContext.Provider>
  )
}

type TooltipFieldWrapperProps = {
  label?: string
  tooltipText?: string
  children: ReactNode
  fieldId?: string
}

let idCounter = 0

export const TooltipFieldWrapper = ({
  label,
  tooltipText,
  children,
  fieldId
}: TooltipFieldWrapperProps) => {
  const [localId] = useState(() => {
    idCounter += 1
    return fieldId ?? `tooltip-field-${idCounter}`
  })
  const { activeId, setActiveId } = useContext(TooltipFieldContext)

  const focused = activeId === localId
  const anyFocused = activeId !== null

  const handleFocusCapture = useCallback(() => {
    setActiveId(localId)
  }, [localId, setActiveId])

  const handleBlurCapture = useCallback(() => {
    setActiveId(null)
  }, [setActiveId])

  if (!tooltipText) return children

  const tooltipContent = (
    <div className="grid gap-2">
      <div className="flex items-start gap-1">
        <Questionmark
          className="h-[18px] shrink-0 text-bluePrimary-800"
          fill="currentColor"
        />
        <span className="text-sm font-bold text-bluePrimary-800">{label}</span>
      </div>
      <p className="text-xs text-gray-700">{tooltipText}</p>
    </div>
  )

  const open = anyFocused ? focused : undefined

  return (
    <Tooltip
      tooltipContent={tooltipContent}
      variant="light"
      position="right-start"
      showTooltipArrow={false}
      open={open}
      classNames={{
        trigger: '',
        content: 'z-[99999] ml-6 max-w-64'
      }}
    >
      <div
        onFocusCapture={handleFocusCapture}
        onBlurCapture={handleBlurCapture}
      >
        {children}
      </div>
    </Tooltip>
  )
}
